if (!customElements.get('peek-slideshow')) {
  customElements.define('peek-slideshow', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const on = (target, type, fn, options = {}) => target.addEventListener(type, fn, { ...options, signal: this.controller.signal });
      this.track = this.querySelector('.peek-slideshow__track');
      this.viewport = this.querySelector('.peek-slideshow__viewport');
      this.slides = [...this.track.children].filter(el => !el.hasAttribute('data-clone'));
      this.count = this.slides.length;
      if (this.count < 2) return;
      this.index = 0;
      this.position = 2;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.stopped = this.dataset.autoplay !== 'true' || this.motion.matches || !!window.Shopify?.designMode;
      this.hovered = false;
      this.visible = true;
      this.moving = false;
      this.imagesReady = false;
      this.drag = null;
      this.suppressClick = false;
      this.toggle = this.querySelector('.peek-slideshow__toggle');
      this.dots = [...this.querySelectorAll('[data-index]')];
      this.status = this.querySelector('.peek-slideshow__status');
      const clone = slide => {
        const copy = slide.cloneNode(true);
        copy.setAttribute('data-clone', '');
        copy.setAttribute('aria-hidden', 'true');
        copy.inert = true;
        copy.removeAttribute('id');
        copy.removeAttribute('data-shopify-editor-block');
        copy.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
        copy.querySelectorAll('img').forEach(el => { el.alt = ''; el.fetchPriority = 'low'; });
        return copy;
      };
      this.track.prepend(...this.slides.slice(-2).map(clone));
      this.track.append(...this.slides.slice(0, 2).map(clone));
      this.classList.add('is-enhanced');
      this.viewport.scrollLeft = 0;
      this.querySelector('.peek-slideshow__controls').hidden = false;
      this.measure();
      this.update();
      this.resize = new ResizeObserver(() => { this.finish(); this.measure(); });
      this.resize.observe(this.viewport);
      on(this, 'click', event => {
        if (this.suppressClick) { event.preventDefault(); event.stopPropagation(); this.suppressClick = false; return; }
        const button = event.target.closest('button');
        if (!button) return;
        if (button === this.toggle) { this.stopped = !this.stopped; this.schedule(); }
        else if (button.hasAttribute('data-step')) this.go(this.index + Number(button.dataset.step), true);
        else if (button.hasAttribute('data-index')) this.go(Number(button.dataset.index), true);
      }, { capture: true });
      on(this, 'keydown', event => {
        if (event.target.closest('input,textarea,select')) return;
        const rtl = getComputedStyle(this).direction === 'rtl';
        const step = {ArrowRight: rtl ? -1 : 1, ArrowLeft: rtl ? 1 : -1}[event.key];
        if (step) { event.preventDefault(); this.go(this.index + step, true); }
        else if (event.key === 'Home' || event.key === 'End') { event.preventDefault(); this.go(event.key === 'Home' ? 0 : this.count - 1, true); }
      });
      on(this, 'focusin', event => { if (event.target !== this.toggle) { this.stopped = true; this.schedule(); } });
      on(this, 'pointerenter', event => { if (event.pointerType === 'mouse') { this.hovered = true; this.schedule(); } });
      on(this, 'pointerleave', event => { if (event.pointerType === 'mouse') { this.hovered = false; this.schedule(); } });
      on(document, 'visibilitychange', () => this.schedule());
      on(this.motion, 'change', () => { this.stopped = true; this.finish(); this.schedule(); });
      on(this.track, 'transitionend', event => { if (event.target === this.track && event.propertyName === 'transform') this.finish(); });
      on(this.viewport, 'dragstart', event => event.preventDefault());
      on(this.viewport, 'pointerdown', event => {
        if (!event.isPrimary || event.button !== 0 || this.moving) return;
        this.suppressClick = false;
        this.drag = {id: event.pointerId, x: event.clientX, y: event.clientY, dx: 0, active: false};
        clearTimeout(this.timer);
      });
      on(this.viewport, 'pointermove', event => {
        const drag = this.drag;
        if (!drag || drag.id !== event.pointerId) return;
        const dx = event.clientX - drag.x;
        const dy = event.clientY - drag.y;
        if (!drag.active && Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 8) { this.drag = null; this.schedule(); return; }
        if (!drag.active && Math.abs(dx) > 8) { drag.active = true; this.viewport.setPointerCapture(event.pointerId); this.classList.add('is-dragging'); }
        if (drag.active) { event.preventDefault(); drag.dx = dx; this.place(false, dx); }
      });
      const release = event => {
        const drag = this.drag;
        if (!drag || drag.id !== event.pointerId) return;
        this.drag = null;
        this.classList.remove('is-dragging');
        if (this.viewport.hasPointerCapture(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
        if (drag.active) {
          this.suppressClick = true;
          clearTimeout(this.clickTimer);
          this.clickTimer = setTimeout(() => { this.suppressClick = false; }, 0);
          const rtl = getComputedStyle(this).direction === 'rtl';
          const step = Math.abs(drag.dx) > Math.min(60, this.step * .15) && event.type !== 'pointercancel' ? (drag.dx < 0 ? 1 : -1) * (rtl ? -1 : 1) : 0;
          this.go(this.index + step, true);
        } else this.schedule();
      };
      on(this.viewport, 'pointerup', release);
      on(this.viewport, 'pointercancel', release);
      on(this.viewport, 'lostpointercapture', event => { if (event.target === this.viewport) release(event); });
      on(document, 'shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        const index = this.slides.findIndex(slide => slide.dataset.blockId === event.detail?.blockId);
        if (index >= 0) { this.finish(); this.go(index, true, false); }
      });
      this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.schedule(); });
      this.observer.observe(this);
      const signal = this.controller.signal;
      Promise.all(this.slides.flatMap(slide => [...slide.querySelectorAll('img')]).map(img => img.decode().catch(() => {}))).then(() => {
        if (signal.aborted) return;
        this.imagesReady = true;
        this.schedule();
      });
      this.schedule();
    }
    measure() {
      this.step = this.slides[0].getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap);
      this.place(false);
    }
    place(animate, drag = 0) {
      const direction = getComputedStyle(this).direction === 'rtl' ? 1 : -1;
      this.track.style.transition = animate && !this.motion.matches ? 'transform 420ms ease' : 'none';
      this.track.style.transform = `translateX(${direction * this.position * this.step + drag}px)`;
    }
    go(target, manual = false, animate = true) {
      if (this.moving) return;
      if (manual) this.stopped = true;
      clearTimeout(this.timer);
      if (this.slides[this.index].contains(document.activeElement)) this.viewport.focus({preventScroll: true});
      this.index = (target + this.count) % this.count;
      this.position = target < 0 ? 1 : target >= this.count ? this.count + 2 : this.index + 2;
      this.moving = animate && !this.motion.matches;
      this.place(animate);
      this.update();
      if (manual) this.status.textContent = this.slides[this.index].getAttribute('aria-label');
      if (this.moving) this.finishTimer = setTimeout(() => this.finish(), 480);
      else this.finish();
    }
    finish() {
      clearTimeout(this.finishTimer);
      this.position = this.index + 2;
      this.moving = false;
      this.update();
      this.place(false);
      this.schedule();
    }
    update() {
      [...this.track.children].forEach((slide, position) => {
        slide.toggleAttribute('data-content-active', !this.moving && position === this.position);
      });
      this.slides.forEach((slide, index) => {
        slide.inert = index !== this.index;
        slide.setAttribute('aria-hidden', String(index !== this.index));
      });
      this.dots.forEach((dot, index) => {
        if (index === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }
    schedule() {
      clearTimeout(this.timer);
      if (!this.toggle) return;
      const label = this.toggle.querySelector('[data-playback-label]') || this.toggle;
      label.textContent = this.stopped ? this.toggle.dataset.play : this.toggle.dataset.pause;
      this.toggle.dataset.paused = String(this.stopped);
      if (!this.isConnected || !this.imagesReady || this.stopped || this.hovered || !this.visible || document.hidden || this.drag || this.moving) return;
      this.timer = setTimeout(() => this.go(this.index + 1), Math.max(1, Number(this.dataset.speed) || 8) * 1000);
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.resize?.disconnect();
      this.observer?.disconnect();
      clearTimeout(this.timer);
      clearTimeout(this.finishTimer);
      clearTimeout(this.clickTimer);
      this.querySelectorAll('[data-clone]').forEach(el => el.remove());
      this.slides?.forEach(slide => { slide.inert = false; slide.removeAttribute('aria-hidden'); });
      this.classList.remove('is-enhanced', 'is-dragging');
      this.track?.removeAttribute('style');
    }
  });
}
